import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Music, Trash2, Play, Copy, Edit, Clock, Dumbbell, Save, X, Upload, FileSpreadsheet } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import * as XLSX from 'xlsx';

const GRUPOS_MUSCULARES = [
  "Tren inferior",
  "Tren superior", 
  "Full body"
];

const EJERCICIOS_TREN_SUPERIOR = [
  "Burpees",
  "Flexiones",
  "Flexiones dragón",
  "Planchas",
  "Encogimientos",
  "Serruchos",
  "Press hombros",
  "Bear crawl",
  "Otro"
];

const EJERCICIOS_TREN_INFERIOR = [
  "Sentadilla",
  "Sentadilla salto",
  "Sentadilla gemelo",
  "Media sentadilla",
  "Cuarto sentadilla",
  "Lunges",
  "Lunges con salto",
  "Skipping",
  "Mountain climbers",
  "Gorilla squat",
  "Otro"
];

const EJERCICIOS_FULL_BODY = [...EJERCICIOS_TREN_SUPERIOR.filter(e => e !== "Otro"), ...EJERCICIOS_TREN_INFERIOR.filter(e => e !== "Otro"), "Otro"];

const TIPOS_BLOQUE = [
  "Activación",
  "Movilidad",
  "Trabajo",
  "Descanso",
  "Cierre"
];

function MKTitan() {
  const { user } = useAuth();
  const [entrenos, setEntrenos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCrear, setShowCrear] = useState(false);
  const [showEditar, setShowEditar] = useState(false);
  const [entrenoActual, setEntrenoActual] = useState(null);
  const [showReproducir, setShowReproducir] = useState(false);
  
  // Reproductor mejorado
  const [isPlaying, setIsPlaying] = useState(false);
  const [tiempoActual, setTiempoActual] = useState(0);
  const [bloqueActual, setBloqueActual] = useState(null);
  const [bloqueSiguiente, setBloqueSiguiente] = useState(null);
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [modoReproduccion, setModoReproduccion] = useState('manual'); // 'manual', 'audio', 'spotify'
  
  // Formulario
  const [formData, setFormData] = useState({
    titulo_cancion: '',
    artista: '',
    duracion_minutos: 0,
    duracion_segundos: 0,
    grupo_muscular: '',
    spotify_url: '',
    bloques: []
  });
  
  // Bloque actual para añadir
  const [nuevoBloque, setNuevoBloque] = useState({
    orden: 1,
    tipo_bloque: 'Trabajo',
    inicio_min: 0,
    inicio_seg: 0,
    fin_min: 0,
    fin_seg: 30,
    ejercicio: '',
    ejercicioCustom: false,
    notas: ''
  });

  useEffect(() => {
    fetchEntrenos();
  }, []);

  // Actualizar bloque actual y siguiente según el tiempo
  useEffect(() => {
    if (entrenoActual && showReproducir) {
      const bloques = entrenoActual.bloques || [];
      const bloqueActualIdx = bloques.findIndex(b => 
        tiempoActual >= b.tiempo_inicio && tiempoActual < b.tiempo_fin
      );
      
      if (bloqueActualIdx >= 0) {
        setBloqueActual(bloques[bloqueActualIdx]);
        // Obtener el siguiente bloque
        setBloqueSiguiente(bloques[bloqueActualIdx + 1] || null);
      } else {
        setBloqueActual(null);
        // Si no hay bloque actual, el siguiente es el primero que viene
        const siguiente = bloques.find(b => tiempoActual < b.tiempo_inicio);
        setBloqueSiguiente(siguiente || null);
      }
    }
  }, [tiempoActual, entrenoActual, showReproducir]);

  // Limpiar timer al cerrar
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const fetchEntrenos = async () => {
    try {
      const res = await axios.get(`${API}/titan/entrenos`);
      setEntrenos(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCrear = async () => {
    const duracionTotal = (formData.duracion_minutos * 60) + formData.duracion_segundos;
    const entrenoData = {
      titulo_cancion: formData.titulo_cancion,
      artista: formData.artista,
      duracion_cancion: duracionTotal,
      grupo_muscular: formData.grupo_muscular,
      spotify_url: formData.spotify_url,
      bloques: formData.bloques
    };
    
    try {
      await axios.post(`${API}/titan/entrenos`, entrenoData);
      setShowCrear(false);
      resetForm();
      fetchEntrenos();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEditar = async () => {
    const duracionTotal = (formData.duracion_minutos * 60) + formData.duracion_segundos;
    const entrenoData = {
      titulo_cancion: formData.titulo_cancion,
      artista: formData.artista,
      duracion_cancion: duracionTotal,
      grupo_muscular: formData.grupo_muscular,
      spotify_url: formData.spotify_url,
      bloques: formData.bloques
    };
    
    try {
      await axios.put(`${API}/titan/entrenos/${entrenoActual.id}`, entrenoData);
      setShowEditar(false);
      resetForm();
      fetchEntrenos();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Eliminar este entreno?')) return;
    try {
      await axios.delete(`${API}/titan/entrenos/${id}`);
      fetchEntrenos();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDuplicar = async (id) => {
    try {
      await axios.post(`${API}/titan/entrenos/${id}/duplicar`);
      fetchEntrenos();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetForm = () => {
    setFormData({
      titulo_cancion: '',
      artista: '',
      duracion_minutos: 0,
      duracion_segundos: 0,
      grupo_muscular: '',
      spotify_url: '',
      bloques: []
    });
    setNuevoBloque({
      orden: 1,
      tipo_bloque: 'Trabajo',
      inicio_min: 0,
      inicio_seg: 0,
      fin_min: 0,
      fin_seg: 30,
      ejercicio: '',
      ejercicioCustom: false,
      notas: ''
    });
  };

  const abrirEditar = (entreno) => {
    setEntrenoActual(entreno);
    const mins = Math.floor(entreno.duracion_cancion / 60);
    const segs = entreno.duracion_cancion % 60;
    setFormData({
      titulo_cancion: entreno.titulo_cancion,
      artista: entreno.artista,
      duracion_minutos: mins,
      duracion_segundos: segs,
      grupo_muscular: entreno.grupo_muscular,
      spotify_url: entreno.spotify_url || '',
      bloques: entreno.bloques || []
    });
    setShowEditar(true);
  };

  const abrirReproducir = (entreno) => {
    setEntrenoActual(entreno);
    setTiempoActual(0);
    setBloqueActual(null);
    setBloqueSiguiente(null);
    setIsPlaying(false);
    setAudioUrl(null);
    setModoReproduccion('manual');
    setShowReproducir(true);
  };

  const cerrarReproducir = () => {
    setShowReproducir(false);
    setIsPlaying(false);
    setTiempoActual(0);
    setBloqueActual(null);
    setBloqueSiguiente(null);
    if (timerRef.current) clearInterval(timerRef.current);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setAudioUrl(null);
    setModoReproduccion('manual');
  };

  // Control del reproductor - modo manual con temporizador
  const togglePlay = () => {
    if (modoReproduccion === 'audio' && audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        clearInterval(timerRef.current);
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
        timerRef.current = setInterval(() => {
          if (audioRef.current) {
            setTiempoActual(Math.floor(audioRef.current.currentTime));
          }
        }, 100);
      }
    } else {
      // Modo manual
      if (isPlaying) {
        clearInterval(timerRef.current);
        setIsPlaying(false);
      } else {
        setIsPlaying(true);
        timerRef.current = setInterval(() => {
          setTiempoActual(prev => {
            if (prev >= entrenoActual.duracion_cancion) {
              clearInterval(timerRef.current);
              setIsPlaying(false);
              return prev;
            }
            return prev + 1;
          });
        }, 1000);
      }
    }
  };

  const stopPlayer = () => {
    clearInterval(timerRef.current);
    setIsPlaying(false);
    setTiempoActual(0);
    setBloqueActual(null);
    setBloqueSiguiente(null);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  const skipToBloque = (bloque) => {
    setTiempoActual(bloque.tiempo_inicio);
    if (audioRef.current) {
      audioRef.current.currentTime = bloque.tiempo_inicio;
    }
  };
  
  const seekTo = (segundos) => {
    setTiempoActual(segundos);
    if (audioRef.current) {
      audioRef.current.currentTime = segundos;
    }
  };
  
  // Cargar archivo de audio local
  const handleAudioUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setAudioUrl(url);
      setModoReproduccion('audio');
      
      // Crear elemento de audio
      const audio = new Audio(url);
      audio.addEventListener('ended', () => {
        setIsPlaying(false);
        clearInterval(timerRef.current);
      });
      audioRef.current = audio;
    }
  };

  const addBloque = () => {
    const tiempoInicio = (nuevoBloque.inicio_min * 60) + nuevoBloque.inicio_seg;
    const tiempoFin = (nuevoBloque.fin_min * 60) + nuevoBloque.fin_seg;
    
    const bloqueNuevo = {
      orden: formData.bloques.length + 1,
      tipo_bloque: nuevoBloque.tipo_bloque,
      tiempo_inicio: tiempoInicio,
      tiempo_fin: tiempoFin,
      ejercicio: nuevoBloque.ejercicio,
      notas: nuevoBloque.notas
    };
    
    setFormData({
      ...formData,
      bloques: [...formData.bloques, bloqueNuevo]
    });
    
    // Preparar siguiente bloque
    setNuevoBloque({
      ...nuevoBloque,
      orden: formData.bloques.length + 2,
      inicio_min: nuevoBloque.fin_min,
      inicio_seg: nuevoBloque.fin_seg,
      fin_min: nuevoBloque.fin_min,
      fin_seg: nuevoBloque.fin_seg + 30 >= 60 ? (nuevoBloque.fin_seg + 30) - 60 : nuevoBloque.fin_seg + 30,
      ejercicio: '',
      ejercicioCustom: false,
      notas: ''
    });
    if (nuevoBloque.fin_seg + 30 >= 60) {
      setNuevoBloque(prev => ({...prev, fin_min: prev.fin_min + 1}));
    }
  };

  const removeBloque = (index) => {
    const nuevosBloques = formData.bloques.filter((_, i) => i !== index);
    const bloquesReordenados = nuevosBloques.map((b, i) => ({ ...b, orden: i + 1 }));
    setFormData({ ...formData, bloques: bloquesReordenados });
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTipoBloqueColor = (tipo) => {
    switch (tipo) {
      case 'Activación': return 'bg-yellow-600';
      case 'Movilidad': return 'bg-purple-600';
      case 'Trabajo': return 'bg-green-600';
      case 'Descanso': return 'bg-blue-600';
      case 'Cierre': return 'bg-orange-600';
      default: return 'bg-gray-600';
    }
  };

  const getProgresoBloque = () => {
    if (!bloqueActual) return 0;
    const duracionBloque = bloqueActual.tiempo_fin - bloqueActual.tiempo_inicio;
    const tiempoEnBloque = tiempoActual - bloqueActual.tiempo_inicio;
    return Math.min(100, (tiempoEnBloque / duracionBloque) * 100);
  };

  // Obtener ejercicios según grupo muscular
  const getEjerciciosPorGrupo = () => {
    switch (formData.grupo_muscular) {
      case 'Tren superior':
        return EJERCICIOS_TREN_SUPERIOR;
      case 'Tren inferior':
        return EJERCICIOS_TREN_INFERIOR;
      case 'Full body':
        return EJERCICIOS_FULL_BODY;
      default:
        return EJERCICIOS_FULL_BODY;
    }
  };

  // Convertir URL de Spotify a embed (soporta tracks, albums, playlists)
  const getSpotifyEmbedUrl = (url) => {
    if (!url) return null;
    try {
      // Limpiar URL: quitar parámetros y locale (intl-es, etc)
      let cleanUrl = url.split('?')[0]; // Quitar query params
      cleanUrl = cleanUrl.replace(/\/intl-[a-z]{2}\//i, '/'); // Quitar /intl-es/
      
      // Extraer el tipo (track, album, playlist) y el ID
      const match = cleanUrl.match(/open\.spotify\.com\/(track|album|playlist)\/([a-zA-Z0-9]+)/);
      if (match) {
        const [, type, id] = match;
        return `https://open.spotify.com/embed/${type}/${id}?utm_source=generator&theme=0`;
      }
    } catch {
      return null;
    }
    return null;
  };

  // Importar Excel para generar bloques automáticamente
  const handleExcelImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        // Buscar info general del entreno
        let titulo = '';
        let artista = '';
        let grupoMuscular = '';
        let duracionTotal = 0;
        
        // Procesar filas de información
        jsonData.forEach((row) => {
          if (row[0] === 'Título de la canción') titulo = row[1] || '';
          if (row[0] === 'Artista') artista = row[1] || '';
          if (row[0] === 'Grupo muscular trabajado') grupoMuscular = row[1] || '';
          if (row[0] === 'Duración' && typeof row[1] === 'number') {
            // Excel guarda tiempo como fracción del día, formato mm:ss
            // 0.125 en formato mm:ss = 3:00 = 180 segundos
            // Fórmula: valor * 24 * 60 = minutos totales (porque Excel usa fracción de día)
            // Pero si está formateado como mm:ss, hay que multiplicar por 24*60 para obtener segundos
            duracionTotal = Math.round(row[1] * 24 * 60);
          }
        });

        // Procesar bloques (filas que empiezan con número en columna 0)
        const bloques = [];
        jsonData.forEach((row) => {
          const orden = row[0];
          const tipoBloque = row[1];
          const tiempo1 = row[2];
          const tiempo2 = row[3];
          const ejercicio = row[4];
          const notas = row[5];

          // Solo procesar filas con orden numérico
          if (typeof orden === 'number' && tipoBloque && tipoBloque.trim && tipoBloque.trim() !== '') {
            // Convertir tiempo decimal de Excel a segundos
            // Excel formatea como mm:ss, guardado como fracción de día
            // 0.125 formateado como mm:ss = 3:00 = 3 minutos = 180 segundos
            // Para obtener segundos: valor * 24 * 60 * 60 / 60 = valor * 24 * 60
            let tiempoInicioSeg = 0;
            let tiempoFinSeg = 0;
            
            if (typeof tiempo1 === 'number') {
              // Convertir fracción de día a segundos para formato mm:ss
              tiempoInicioSeg = Math.round(tiempo1 * 24 * 60);
            }
            if (typeof tiempo2 === 'number') {
              tiempoFinSeg = Math.round(tiempo2 * 24 * 60);
            } else if (typeof tiempo2 === 'string') {
              // Formato "9_00" o "9:00" = 9 minutos 0 segundos
              const cleanTime = tiempo2.replace('_', ':');
              const parts = cleanTime.split(':');
              if (parts.length === 2) {
                const mins = parseInt(parts[0]) || 0;
                const secs = parseInt(parts[1]) || 0;
                tiempoFinSeg = (mins * 60) + secs;
              }
            }

            // Mapear tipo de bloque
            let tipoMapeado = 'Trabajo';
            const tipoLower = tipoBloque.toLowerCase();
            if (tipoLower.includes('movilidad')) tipoMapeado = 'Movilidad';
            else if (tipoLower.includes('trabajo')) tipoMapeado = 'Trabajo';
            else if (tipoLower.includes('descanso')) tipoMapeado = 'Descanso';
            else if (tipoLower.includes('activacion') || tipoLower.includes('entrada')) tipoMapeado = 'Activación';
            else if (tipoLower.includes('cierre')) tipoMapeado = 'Cierre';

            bloques.push({
              orden: orden,
              tipo_bloque: tipoMapeado,
              tiempo_inicio: tiempoInicioSeg,
              tiempo_fin: tiempoFinSeg,
              ejercicio: ejercicio || '-',
              notas: notas || ''
            });
          }
        });

        // Calcular duración total si no se proporcionó
        if (!duracionTotal && bloques.length > 0) {
          duracionTotal = Math.max(...bloques.map(b => b.tiempo_fin));
        }

        // Actualizar formulario
        setFormData(prev => ({
          ...prev,
          titulo_cancion: titulo || prev.titulo_cancion,
          artista: artista || prev.artista,
          grupo_muscular: grupoMuscular || prev.grupo_muscular,
          duracion_minutos: Math.floor(duracionTotal / 60),
          duracion_segundos: duracionTotal % 60,
          bloques: bloques
        }));

        alert(`✅ Excel importado:\n• Canción: ${titulo}\n• Artista: ${artista}\n• ${bloques.length} bloques generados`);
      } catch (error) {
        console.error('Error al importar Excel:', error);
        alert('❌ Error al importar el archivo Excel: ' + error.message);
      }
    };
    reader.readAsArrayBuffer(file);
    // Reset input
    event.target.value = '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950">
        <Navbar />
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950">
      <Navbar />
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gold flex items-center gap-2">
              <Music className="h-8 w-8" /> MK Titan
            </h1>
            <p className="text-gray-400">Entrenos sincronizados con música</p>
          </div>
          {user?.role === 'entrenador' && (
            <Button onClick={() => setShowCrear(true)} className="btn-primary-mk">
              <Plus className="h-4 w-4 mr-2" /> Nuevo
            </Button>
          )}
        </div>

        {/* Lista de Entrenos */}
        {entrenos.length === 0 ? (
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-12 text-center">
              <Music className="h-16 w-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">No hay entrenos musicales</p>
              {user?.role === 'entrenador' && (
                <Button onClick={() => setShowCrear(true)} className="btn-primary-mk mt-4">
                  <Plus className="h-4 w-4 mr-2" /> Crear el primero
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {entrenos.map((entreno) => (
              <Card key={entreno.id} className="bg-gray-900 border-gray-800 hover:border-gold/50 transition-all">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-gold text-lg">{entreno.titulo_cancion}</CardTitle>
                      <p className="text-gray-400 text-sm">{entreno.artista}</p>
                    </div>
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => abrirReproducir(entreno)}
                    >
                      <Play className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4 text-sm text-gray-300 mb-3">
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" /> {formatTime(entreno.duracion_cancion)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Dumbbell className="h-4 w-4" /> {entreno.grupo_muscular}
                    </span>
                  </div>
                  <p className="text-gray-500 text-sm">{entreno.bloques?.length || 0} bloques</p>
                  
                  {user?.role === 'entrenador' && (
                    <div className="flex gap-2 mt-3 pt-3 border-t border-gray-800">
                      <Button size="sm" variant="outline" className="flex-1 border-blue-600 text-blue-400" onClick={() => abrirEditar(entreno)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" className="border-purple-600 text-purple-400" onClick={() => handleDuplicar(entreno.id)}>
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" className="border-red-600 text-red-400" onClick={() => handleEliminar(entreno.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Dialog Crear/Editar */}
        <Dialog open={showCrear || showEditar} onOpenChange={(open) => {
          if (!open) { setShowCrear(false); setShowEditar(false); resetForm(); }
        }}>
          <DialogContent className="bg-gray-900 text-white border-gray-700 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-gold flex items-center justify-between">
                <span>{showEditar ? 'Editar Entreno' : 'Nuevo Entreno Musical'}</span>
                {/* Botón importar Excel */}
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleExcelImport}
                    className="hidden"
                  />
                  <Button variant="outline" size="sm" className="border-green-600 text-green-400 hover:bg-green-600/20" asChild>
                    <span>
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                      Importar Excel
                    </span>
                  </Button>
                </label>
              </DialogTitle>
            </DialogHeader>
            
            <Tabs defaultValue="info" className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-gray-800 mb-4">
                <TabsTrigger value="info">📋 Info</TabsTrigger>
                <TabsTrigger value="bloques">🎵 Bloques ({formData.bloques.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="info" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Canción</Label>
                    <Input
                      value={formData.titulo_cancion}
                      onChange={(e) => setFormData({...formData, titulo_cancion: e.target.value})}
                      placeholder="Ej: OPUS"
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Artista</Label>
                    <Input
                      value={formData.artista}
                      onChange={(e) => setFormData({...formData, artista: e.target.value})}
                      placeholder="Ej: Eric Prydz"
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2 col-span-2">
                    <Label>Duración</Label>
                    <div className="flex gap-2 items-center">
                      <Input
                        type="number"
                        min="0"
                        value={formData.duracion_minutos}
                        onChange={(e) => setFormData({...formData, duracion_minutos: parseInt(e.target.value) || 0})}
                        className="bg-gray-800 border-gray-700 w-20"
                      />
                      <span className="text-gray-400">min</span>
                      <Input
                        type="number"
                        min="0"
                        max="59"
                        value={formData.duracion_segundos}
                        onChange={(e) => setFormData({...formData, duracion_segundos: parseInt(e.target.value) || 0})}
                        className="bg-gray-800 border-gray-700 w-20"
                      />
                      <span className="text-gray-400">seg</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Grupo muscular</Label>
                    <Select value={formData.grupo_muscular} onValueChange={(v) => setFormData({...formData, grupo_muscular: v})}>
                      <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                        <SelectValue placeholder="Elegir..." />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700 text-white">
                        {GRUPOS_MUSCULARES.map(g => (
                          <SelectItem key={g} value={g}>{g}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Campo Spotify */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <span className="text-green-500">🎵</span> Enlace de Spotify (opcional)
                  </Label>
                  <Input
                    value={formData.spotify_url}
                    onChange={(e) => setFormData({...formData, spotify_url: e.target.value})}
                    placeholder="https://open.spotify.com/track/..."
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                  <p className="text-xs text-gray-500">Pega el enlace de la canción de Spotify para reproducirla durante el entreno</p>
                </div>
              </TabsContent>

              <TabsContent value="bloques" className="space-y-4">
                {/* Añadir bloque */}
                <Card className="bg-gray-800 border-gray-700 text-white">
                  <CardContent className="p-3 space-y-3">
                    <div className="grid grid-cols-4 gap-2">
                      <div>
                        <Label className="text-xs">Tipo</Label>
                        <Select value={nuevoBloque.tipo_bloque} onValueChange={(v) => setNuevoBloque({...nuevoBloque, tipo_bloque: v})}>
                          <SelectTrigger className="bg-gray-700 border-gray-600 h-8 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-700 border-gray-600">
                            {TIPOS_BLOQUE.map(t => (
                              <SelectItem key={t} value={t}>{t}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Inicio</Label>
                        <div className="flex gap-1">
                          <Input type="number" min="0" value={nuevoBloque.inicio_min} onChange={(e) => setNuevoBloque({...nuevoBloque, inicio_min: parseInt(e.target.value) || 0})} className="bg-gray-700 border-gray-600 h-8 text-sm w-12 px-1" />
                          <span className="text-gray-400 text-xs self-center">:</span>
                          <Input type="number" min="0" max="59" value={nuevoBloque.inicio_seg} onChange={(e) => setNuevoBloque({...nuevoBloque, inicio_seg: parseInt(e.target.value) || 0})} className="bg-gray-700 border-gray-600 h-8 text-sm w-12 px-1" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Fin</Label>
                        <div className="flex gap-1">
                          <Input type="number" min="0" value={nuevoBloque.fin_min} onChange={(e) => setNuevoBloque({...nuevoBloque, fin_min: parseInt(e.target.value) || 0})} className="bg-gray-700 border-gray-600 h-8 text-sm w-12 px-1" />
                          <span className="text-gray-400 text-xs self-center">:</span>
                          <Input type="number" min="0" max="59" value={nuevoBloque.fin_seg} onChange={(e) => setNuevoBloque({...nuevoBloque, fin_seg: parseInt(e.target.value) || 0})} className="bg-gray-700 border-gray-600 h-8 text-sm w-12 px-1" />
                        </div>
                      </div>
                      <div className="flex items-end">
                        <Button onClick={addBloque} size="sm" className="w-full btn-primary-mk h-8">
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Select 
                        value={nuevoBloque.ejercicio} 
                        onValueChange={(v) => {
                          if (v === 'Otro') {
                            setNuevoBloque({...nuevoBloque, ejercicio: '', ejercicioCustom: true});
                          } else {
                            setNuevoBloque({...nuevoBloque, ejercicio: v, ejercicioCustom: false});
                          }
                        }}
                      >
                        <SelectTrigger className="bg-gray-700 border-gray-600 h-8 text-sm">
                          <SelectValue placeholder="Ejercicio..." />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-700 max-h-60">
                          {getEjerciciosPorGrupo().map(ej => (
                            <SelectItem key={ej} value={ej} className="text-white">{ej}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        value={nuevoBloque.notas}
                        onChange={(e) => setNuevoBloque({...nuevoBloque, notas: e.target.value})}
                        placeholder="Notas"
                        className="bg-gray-700 border-gray-600 h-8 text-sm"
                      />
                    </div>
                    {/* Input personalizado si elige "Otro" */}
                    {nuevoBloque.ejercicioCustom && (
                      <Input
                        value={nuevoBloque.ejercicio}
                        onChange={(e) => setNuevoBloque({...nuevoBloque, ejercicio: e.target.value})}
                        placeholder="Escribe el ejercicio..."
                        className="bg-gray-700 border-gray-600 h-8 text-sm mt-2"
                        autoFocus
                      />
                    )}
                  </CardContent>
                </Card>

                {/* Lista bloques */}
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {formData.bloques.map((bloque, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-gray-800 rounded-lg">
                      <span className={`px-2 py-1 rounded text-xs text-white ${getTipoBloqueColor(bloque.tipo_bloque)}`}>
                        {bloque.tipo_bloque}
                      </span>
                      <span className="text-gray-400 text-xs font-mono">
                        {formatTime(bloque.tiempo_inicio)}-{formatTime(bloque.tiempo_fin)}
                      </span>
                      <span className="text-white text-sm flex-1 truncate">{bloque.ejercicio}</span>
                      <Button size="sm" variant="ghost" className="text-red-400 h-6 w-6 p-0" onClick={() => removeBloque(index)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>

            <div className="flex gap-2 pt-4 border-t border-gray-700">
              <Button variant="outline" className="flex-1 border-gray-600" onClick={() => { setShowCrear(false); setShowEditar(false); resetForm(); }}>
                Cancelar
              </Button>
              <Button className="flex-1 btn-primary-mk" onClick={showEditar ? handleEditar : handleCrear} disabled={!formData.titulo_cancion || !formData.artista}>
                <Save className="h-4 w-4 mr-2" /> Guardar
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog Reproductor - Modo Visual con temporizador sincronizado */}
        <Dialog open={showReproducir} onOpenChange={cerrarReproducir}>
          <DialogContent className="bg-gray-950 text-white border-gray-700 max-w-lg p-0 overflow-hidden max-h-[90vh] overflow-y-auto">
            {entrenoActual && (
              <>
                {/* Header con info */}
                <div className="bg-gradient-to-b from-gray-800 to-gray-900 p-3">
                  <h2 className="text-gold font-bold text-lg">{entrenoActual.titulo_cancion}</h2>
                  <p className="text-gray-400 text-sm">{entrenoActual.artista} • {entrenoActual.grupo_muscular}</p>
                </div>
                
                {/* EJERCICIO ACTUAL - ARRIBA EN GRANDE */}
                <div className={`mx-4 mt-4 p-6 rounded-xl border-4 text-center ${
                  bloqueActual 
                    ? `${getTipoBloqueColor(bloqueActual.tipo_bloque)} animate-pulse` 
                    : 'bg-gray-800 border-gray-600'
                }`}>
                  <p className="text-gray-300 text-xs mb-1">🎯 EJERCICIO ACTUAL</p>
                  <p className="text-white font-bold text-3xl">
                    {bloqueActual ? (bloqueActual.ejercicio || bloqueActual.tipo_bloque) : '⏳ Esperando...'}
                  </p>
                  {bloqueActual && (
                    <Badge className={`mt-2 ${getTipoBloqueColor(bloqueActual.tipo_bloque)}`}>
                      {bloqueActual.tipo_bloque}
                    </Badge>
                  )}
                </div>
                
                {/* TIEMPO ACTUAL - EN EL CENTRO */}
                <div className="p-4 text-center">
                  <div className="text-6xl font-mono font-bold text-gold">
                    {formatTime(tiempoActual)}
                  </div>
                  <div className="text-gray-500 text-sm">
                    de {formatTime(entrenoActual.duracion_cancion)}
                  </div>
                  
                  {/* Barra de progreso */}
                  <div className="w-full bg-gray-800 rounded-full h-3 mt-3 overflow-hidden">
                    <div 
                      className="bg-gold h-3 rounded-full transition-all duration-100"
                      style={{ width: `${(tiempoActual / entrenoActual.duracion_cancion) * 100}%` }}
                    />
                  </div>
                  
                  {/* Controles */}
                  <div className="flex justify-center gap-4 mt-4">
                    <Button
                      size="lg"
                      variant="outline"
                      className="border-gray-600 text-white"
                      onClick={stopPlayer}
                    >
                      <X className="h-5 w-5" />
                    </Button>
                    <Button
                      size="lg"
                      className={isPlaying ? 'bg-orange-600 hover:bg-orange-700' : 'bg-green-600 hover:bg-green-700'}
                      onClick={togglePlay}
                    >
                      {isPlaying ? '⏸️ Pausar' : '▶️ Iniciar'}
                    </Button>
                  </div>
                </div>
                
                {/* SIGUIENTE EJERCICIO */}
                <div className="px-4 pb-2">
                  <div className="bg-gray-800/50 rounded-xl p-3 border border-gray-700">
                    <p className="text-gray-400 text-xs text-center">📋 SIGUIENTE</p>
                    {bloqueSiguiente ? (
                      <div className="text-center">
                        <p className="text-white font-bold text-lg">{bloqueSiguiente.ejercicio || bloqueSiguiente.tipo_bloque}</p>
                        <p className="text-gold text-sm">en {formatTime(bloqueSiguiente.tiempo_inicio - tiempoActual)}</p>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center text-sm">No hay más bloques</p>
                    )}
                  </div>
                </div>
                
                {/* OPCIÓN DE AUDIO LOCAL - ABAJO */}
                <div className="px-4 pb-4">
                  <div className="bg-gray-800 rounded-lg p-3 border border-purple-900">
                    <p className="text-purple-400 text-xs mb-2 text-center font-semibold">🎵 Música sincronizada</p>
                    <label className="block">
                      <input 
                        type="file" 
                        accept="audio/*" 
                        onChange={handleAudioUpload}
                        className="hidden"
                      />
                      <Button variant="outline" className="w-full border-purple-600 text-purple-400 hover:bg-purple-900/30" asChild>
                        <span className="cursor-pointer">
                          <Upload className="h-4 w-4 mr-2" />
                          {audioUrl ? '✅ MP3 cargado - Sincronizado' : 'Cargar MP3 para sincronizar'}
                        </span>
                      </Button>
                    </label>
                    {!audioUrl && entrenoActual.spotify_url && (
                      <p className="text-gray-500 text-xs text-center mt-2">
                        💡 Sube el MP3 de la canción para sincronización perfecta
                      </p>
                    )}
                  </div>
                </div>

                {/* Botón cerrar */}
                <div className="bg-gray-900 p-3 border-t border-gray-800">
                  <Button 
                    className="w-full bg-gray-700 hover:bg-gray-600"
                    onClick={cerrarReproducir}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cerrar
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

export default MKTitan;
